import api, { v2BaseUrl } from "../api";
import { DashboardFilter } from "../../types/dashboard";
import { buildAssetTreePayload } from "@utils/assetTreePayload";
import {
  MAINTENANCE_ALERT_TYPES,
  MaintenanceAlert,
  MaintenanceAlertDetail,
  MaintenanceAlertMetric,
  MaintenanceAlertTotals,
  MaintenanceAlertType,
  MaintenanceSubcategoryTotal,
  MaintenanceSummary,
  MaintenanceSummaryMetric,
  MaintenanceTopVehiclesOrder,
  MaintenanceTransaction,
  MaintenanceTypeTotal,
  MaintenanceVehicleCost,
} from "./types";

export { MAINTENANCE_ALERT_TYPES } from "./types";

export type {
  MaintenanceAlert,
  MaintenanceAlertDetail,
  MaintenanceAlertMetric,
  MaintenanceAlertSeverity,
  MaintenanceAlertTotals,
  MaintenanceAlertType,
  MaintenanceNullableSummaryMetric,
  MaintenanceStatus,
  MaintenanceSubcategoryTotal,
  MaintenanceSummary,
  MaintenanceSummaryMetric,
  MaintenanceTopVehiclesOrder,
  MaintenanceTransaction,
  MaintenanceType,
  MaintenanceTypeTotal,
  MaintenanceVehicleCost,
} from "./types";

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_TOP_LIMIT = 5;

export type MaintenancePeriod = Pick<DashboardFilter, "startDate" | "endDate">;

export interface MaintenancePage<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

const emptyPage = <T>(page: number, size: number): MaintenancePage<T> => ({
  content: [],
  page,
  size,
  totalElements: 0,
  totalPages: 0,
});

const normalizePage = <T>(
  data: any,
  page: number,
  size: number,
  normalizeItem: (item: any) => T
): MaintenancePage<T> => ({
  content: Array.isArray(data?.content) ? data.content.map(normalizeItem) : [],
  page: Number(data?.page ?? page),
  size: Number(data?.size ?? size),
  totalElements: Number(data?.totalElements ?? 0),
  totalPages: Number(data?.totalPages ?? 0),
});

const EMPTY_METRIC: MaintenanceSummaryMetric = {
  value: 0,
  previousValue: 0,
  variationPercent: null,
  currentPeriod: null,
  previousPeriod: null,
};

export const EMPTY_MAINTENANCE_SUMMARY: MaintenanceSummary = {
  totalSpent: EMPTY_METRIC,
  averageCostPerKm: { ...EMPTY_METRIC, value: null, previousValue: null, kmDriven: 0 },
  averageCostPerVehicle: EMPTY_METRIC,
  totalVehicles: EMPTY_METRIC,
};

const toNullableNumber = (value: unknown): number | null => (value == null ? null : Number(value));

const normalizeMetric = (metric: any): MaintenanceSummaryMetric => ({
  value: Number(metric?.value ?? 0),
  previousValue: Number(metric?.previousValue ?? 0),
  variationPercent: toNullableNumber(metric?.variationPercent),
  currentPeriod: metric?.currentPeriod ?? null,
  previousPeriod: metric?.previousPeriod ?? null,
});

const normalizeTransaction = (item: any): MaintenanceTransaction => ({
  id: item?.id,
  dateTime: item?.dateTime,
  vehicle: item?.vehicle,
  type: item?.type,
  subcategory: item?.subcategory,
  paymentMethod: item?.paymentMethod,
  amount: Number(item?.amount ?? 0),
  status: item?.status,
});

const normalizeAlertTotals = (data: any): MaintenanceAlertTotals => ({
  total: Number(data?.total ?? 0),
  alerts: Object.fromEntries(MAINTENANCE_ALERT_TYPES.map((type) => [type, Number(data?.alerts?.[type] ?? 0)])),
});

const normalizeAlert = (item: any): MaintenanceAlert => ({
  id: item?.id,
  type: item?.type,
  severity: item?.severity,
  title: item?.title,
  description: item?.description,
  occurredAt: item?.occurredAt,
});

const normalizeAlertMetric = (metric: any): MaintenanceAlertMetric => ({
  label: metric?.label,
  value: Number(metric?.value ?? 0),
  highlighted: Boolean(metric?.highlighted),
});

const buildPeriodBody = (period: MaintenancePeriod) => ({
  startDate: period.startDate,
  endDate: period.endDate,
});

const buildPeriodFilterBody = (filters: DashboardFilter) => ({
  ...buildPeriodBody(filters),
  assetTree: buildAssetTreePayload(filters),
});

const fetchRanking = async (
  endpoint: string,
  filters: DashboardFilter,
  limit: number,
  extraBody: Record<string, unknown> = {}
): Promise<any[]> => {
  const response = await api.post(`${v2BaseUrl}/maintenances/${endpoint}`, {
    ...buildPeriodFilterBody(filters),
    limit,
    ...extraBody,
  });
  const items = response.data?.data;
  return Array.isArray(items) ? items : [];
};

export const getMaintenanceSummary = async (filters: DashboardFilter): Promise<MaintenanceSummary> => {
  try {
    const response = await api.post(`${v2BaseUrl}/maintenances/summary`, buildPeriodFilterBody(filters));
    const data = response.data?.data ?? {};

    return {
      totalSpent: normalizeMetric(data.totalSpent),
      averageCostPerKm: {
        ...normalizeMetric(data.averageCostPerKm),
        value: toNullableNumber(data.averageCostPerKm?.value),
        previousValue: toNullableNumber(data.averageCostPerKm?.previousValue),
        kmDriven: Number(data.averageCostPerKm?.kmDriven ?? 0),
      },
      averageCostPerVehicle: normalizeMetric(data.averageCostPerVehicle),
      totalVehicles: normalizeMetric(data.totalVehicles),
    };
  } catch (error) {
    console.error("Error fetching maintenance summary:", error);
    return EMPTY_MAINTENANCE_SUMMARY;
  }
};

export const getMaintenanceByType = async (filters: DashboardFilter): Promise<MaintenanceTypeTotal[]> => {
  try {
    const response = await api.post(`${v2BaseUrl}/maintenances/by-type`, buildPeriodFilterBody(filters));
    const items = response.data?.data;

    if (!Array.isArray(items)) return [];

    return items.map((item) => ({
      type: item?.type,
      amount: Number(item?.amount ?? 0),
    }));
  } catch (error) {
    console.error("Error fetching maintenance totals by type:", error);
    return [];
  }
};

export const getTopMaintenanceSubcategories = async (
  filters: DashboardFilter,
  limit = DEFAULT_TOP_LIMIT
): Promise<MaintenanceSubcategoryTotal[]> => {
  try {
    const items = await fetchRanking("top-subcategories", filters, limit);

    return items.map((item) => ({
      subcategory: item?.subcategory,
      amount: Number(item?.amount ?? 0),
    }));
  } catch (error) {
    console.error("Error fetching maintenance top subcategories:", error);
    return [];
  }
};

export const getTopMaintenanceVehicles = async (
  filters: DashboardFilter,
  limit = DEFAULT_TOP_LIMIT,
  order?: MaintenanceTopVehiclesOrder
): Promise<MaintenanceVehicleCost[]> => {
  try {
    const items = await fetchRanking("top-vehicles", filters, limit, order ? { order } : {});

    return items.map((item) => ({
      vehicle: item?.vehicle,
      amount: Number(item?.amount ?? 0),
      costPerKm: toNullableNumber(item?.costPerKm),
    }));
  } catch (error) {
    console.error("Error fetching maintenance top vehicles:", error);
    return [];
  }
};

export const getMaintenanceTransactions = async (
  filters: DashboardFilter,
  page = 0,
  size = DEFAULT_PAGE_SIZE,
  search = ""
): Promise<MaintenancePage<MaintenanceTransaction>> => {
  try {
    const response = await api.post(`${v2BaseUrl}/maintenances`, {
      ...buildPeriodFilterBody(filters),
      page,
      size,
      search: search.trim() || undefined,
    });
    return normalizePage(response.data?.data, page, size, normalizeTransaction);
  } catch (error) {
    console.error("Error fetching maintenance transactions:", error);
    return emptyPage(page, size);
  }
};

export const getMaintenanceAlertTotals = async (filters: DashboardFilter): Promise<MaintenanceAlertTotals> => {
  try {
    const response = await api.post(`${v2BaseUrl}/maintenances/alerts`, buildPeriodFilterBody(filters));
    return normalizeAlertTotals(response.data?.data);
  } catch (error) {
    console.error("Error fetching maintenance alert totals:", error);
    return normalizeAlertTotals(null);
  }
};

export const getMaintenanceAlertHistory = async (
  filters: DashboardFilter,
  type?: MaintenanceAlertType,
  page = 0,
  size = DEFAULT_PAGE_SIZE
): Promise<MaintenancePage<MaintenanceAlert>> => {
  try {
    const response = await api.post(`${v2BaseUrl}/maintenances/alerts/history`, {
      ...buildPeriodFilterBody(filters),
      type,
      page,
      size,
    });
    return normalizePage(response.data?.data, page, size, normalizeAlert);
  } catch (error) {
    console.error("Error fetching maintenance alert history:", error);
    return emptyPage(page, size);
  }
};

export const getMaintenanceAlertDetail = async (
  filters: DashboardFilter,
  alertId: string
): Promise<MaintenanceAlertDetail | null> => {
  try {
    const response = await api.post(`${v2BaseUrl}/maintenances/alerts/detail`, {
      alertId,
      ...buildPeriodFilterBody(filters),
    });
    const data = response.data?.data;

    if (!data) return null;

    return {
      ...normalizeAlert(data),
      metrics: Array.isArray(data.metrics) ? data.metrics.map(normalizeAlertMetric) : [],
    };
  } catch (error) {
    console.error("Error fetching maintenance alert detail:", error);
    return null;
  }
};

export const exportMaintenance = async (_filters: DashboardFilter): Promise<void> => undefined;
